import React, { memo, useCallback, useRef, useState } from "react";

import { ICatalogue } from "../../../apis/useGetCatalogueData/types";
import { doNothing } from "../../../common/helpers/GlobalEvents";
import { displayCentsWithDollarSign } from "../../../common/helpers/stringUtils";
import Condition from "../../../components/Conditional/Condition";
import I18nText from "../../../components/I18nText/I18nText";
import ItemTagLabels from "../../../components/ItemTagLabels/ItemTagLabels";
import ItemTagStar from "../../../components/ItemTagStar/ItemTagStar";
import OOSIndicator from "../../../components/OOSIndicator/OOSIndicator";
import { Image } from "../../../components/elements";
import QuantitySelector from "../../../components/elements/Selection/quantity/QuantitySelector";
import {
	useCartActions,
	useCartContext,
} from "../../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { addDatadogAction, buildLocationContext } from "../../../services/datadog";
import { ICategoryList } from "../types";
import ProductModal from "./ProductModal";
import "./ProductSection.scss";

interface IProductSectionProps {
	category: ICategoryList & { products: ICatalogue[] };
	sectionRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
}

const ProductSection: React.FC<IProductSectionProps> = memo(({ category, sectionRefs }) => {
	const sectionElementRef = useRef<HTMLDivElement | null>(null);
	const { ADD_ITEM, UPDATE_CART_ITEM, REMOVE_CART } = useCartActions();
	const { state: cart } = useCartContext((state) => state.cart);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);
	const [modalProduct, setModalProduct] = useState<ICatalogue | null>(null);

	const handleQuantityChange = useCallback(
		(product: ICatalogue) => (quantity: number) => {
			const cartIndex = cart.findIndex(
				(item) => item.brand_menu_item_id === product.brand_menu_item_id
			);

			if (quantity === 0) {
				if (cartIndex !== -1) {
					REMOVE_CART(cartIndex);
				}
			} else if (cartIndex !== -1) {
				// Update existing item
				const updatedItem = { ...cart[cartIndex], quantity };

				UPDATE_CART_ITEM({ index: cartIndex, item: updatedItem });
			} else {
				// Add new item
				const itemToAdd = { ...product, quantity };

				addDatadogAction("productlist_add_to_cart", {
					...buildLocationContext(location, stall),
					sku: String(product.brand_menu_item_id),
					quantity,
					productName: product.title,
				});
				ADD_ITEM(itemToAdd);
			}
		},
		[ADD_ITEM, UPDATE_CART_ITEM, REMOVE_CART, cart, location, stall]
	);

	const setRefs = useCallback(
		(node: HTMLDivElement | null) => {
			sectionElementRef.current = node;
			sectionRefs.current.set(category.id, node);
		},
		[sectionRefs, category.id]
	);

	return (
		<div ref={setRefs} className="ProductCategorySection" id={`category-${category.id}`}>
			<div className="CategoryHeader">
				<I18nText className="CategoryTitle" enText={category.name} cnText={category.name_cn} />
			</div>
			<div className="ProductGrid">
				{category?.products?.map((product) => {
					const currentQuantity =
						cart.find((item) => item.brand_menu_item_id === product.brand_menu_item_id)?.quantity ||
						0;

					const isLargeItem = product.tags?.includes("sok-large-item") ?? false;
					const isModalItem = product.tags?.includes("sok-modal-item") ?? false;

					const handleProductClick = () => {
						if (isModalItem && product.inventory_count > 0) {
							setModalProduct(product);
						}
					};

					return (
						<div
							key={product.brand_menu_item_id}
							className={`ProductItem ${isLargeItem ? "ProductItem--large" : ""} ${isModalItem ? "ProductItem--modal" : ""}`}
						>
							<button
								data-testid={`product-item-${product.brand_menu_item_id}`}
								className="ProductInfoButton"
								onClick={isModalItem ? handleProductClick : doNothing}
								disabled={product.inventory_count === 0}
							>
								<div className="ProductImageContainer">
									<Condition check={product.inventory_count === 0}>
										<OOSIndicator data-testid={"oos-indicator-" + product.brand_menu_item_id} />
									</Condition>
									<Condition check={product.tags?.includes("sok-star") ?? false}>
										<ItemTagStar />
									</Condition>
									<Image className="ProductItemImage" src={product.product_image} />
								</div>
								<div className="ProductInfo">
									<I18nText
										className="ProductTitle"
										enText={product.title}
										cnText={product.title_cn}
									/>
									<I18nText
										className="ProductDescription"
										enText={product.description}
										cnText={product.description_cn}
									/>
									<ItemTagLabels tags={product.tags} />
									<div className="ProductPriceWrapper">
										<p className="ProductPrice">{displayCentsWithDollarSign(product.price)}</p>
									</div>
								</div>
							</button>
							<div className="ProductControls">
								<QuantitySelector
									max={10}
									onChange={handleQuantityChange(product)}
									initValue={currentQuantity}
									testid={`quantity-selector-${product.brand_menu_item_id}`}
									inventoryCount={product.inventory_count}
									showZero={false}
								/>
							</div>
						</div>
					);
				})}
			</div>
			{modalProduct && (
				<ProductModal
					product={modalProduct}
					currentQuantity={
						cart.find((item) => item.brand_menu_item_id === modalProduct.brand_menu_item_id)
							?.quantity || 0
					}
					onClose={() => setModalProduct(null)}
					onAddToCart={(quantity) => {
						handleQuantityChange(modalProduct)(quantity);
						setModalProduct(null);
					}}
				/>
			)}
		</div>
	);
});

export default ProductSection;
